import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  KNOWLEDGE_DOC_PAGE_NAVIGATE,
  useGeckoAnalytics,
} from 'features/analytics'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { TooltipOption } from 'features/knowledgeDocumentViewer/types'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { MOBILE_BREAKPOINT_PX } from 'utils/mobileLayout'

const PageSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const PageText = styled.span`
  font-size: 0.875rem;
  color: ${(props) => props.theme.forecast.defaultText};
  white-space: nowrap;
  user-select: none;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
`

const PageInput = styled.input<{ $charWidth: number }>`
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.forecast.defaultText};
  background: transparent;
  width: ${(props) => props.$charWidth}ch;
  text-align: center;

  @media (min-width: ${MOBILE_BREAKPOINT_PX}px) {
    font-size: 0.875rem;
  }
`

export function PageSelector() {
  const { t } = useLingui()
  const {
    activeTabId,
    updateDocumentHistoryItem,
    tooltipOptions,
    isOnDocument,
    currentPageNumber,
    currentDocumentId,
    recordViewerAction,
    isEvalSessionLocked,
  } = useKnowledgeDocumentUXContext()
  const { totalPages } = useDocumentTabDataContext()
  const { trackEvent } = useGeckoAnalytics()

  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const multiPageTurner = tooltipOptions.includes(TooltipOption.MultiPageTurner)
  const showMultiplePages = tooltipOptions.includes(
    TooltipOption.ShowMultiplePages
  )
  const pageStep = multiPageTurner ? 2 : 1

  const navigateToPage = useCallback(
    (targetPage: number, direction: string) => {
      const clamped = Math.max(1, Math.min(totalPages, targetPage))
      if (clamped === currentPageNumber) return
      trackEvent(KNOWLEDGE_DOC_PAGE_NAVIGATE.title, {
        direction,
        from_page: currentPageNumber,
        to_page: clamped,
        total_pages: totalPages,
        document_id: currentDocumentId,
      })
      // eslint-disable-next-line lingui/no-unlocalized-strings
      recordViewerAction(`Navigated ${direction} to page ${clamped}`)
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: clamped,
        selectedBoxIds: [],
      })
    },
    [
      activeTabId,
      currentDocumentId,
      currentPageNumber,
      totalPages,
      trackEvent,
      recordViewerAction,
      updateDocumentHistoryItem,
    ]
  )

  const handlePreviousPage = () =>
    navigateToPage(currentPageNumber - pageStep, 'previous')

  const handleNextPage = () =>
    navigateToPage(currentPageNumber + pageStep, 'next')

  const handleStartEdit = () => {
    setInputValue(String(currentPageNumber))
    setIsEditing(true)
  }

  const handleSubmit = () => {
    setIsEditing(false)
    const parsed = parseInt(inputValue, 10)
    if (!isNaN(parsed)) {
      navigateToPage(parsed, 'jump')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsEditing(false)
    }
    // Stop arrow keys from triggering page navigation while typing
    e.stopPropagation()
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Canvas prevents native blur, so manually trigger it on outside clicks
  useEffect(() => {
    if (!isEditing) return
    const onMouseDown = (e: MouseEvent) => {
      if (!inputRef.current?.contains(e.target as Node))
        inputRef.current?.blur()
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [isEditing])

  if (!isOnDocument || totalPages <= 1) {
    return null
  }

  const hasSecondPage =
    showMultiplePages && totalPages > 1 && currentPageNumber < totalPages
  const pageLabel = hasSecondPage ? t`Pages` : t`Page`
  const pageSuffix = hasSecondPage
    ? `-${currentPageNumber + 1} / ${totalPages}`
    : ` / ${totalPages}`

  if (isEvalSessionLocked) return null

  return (
    <PageSelectorContainer>
      <Button
        variant='ghost'
        size='icon-lg'
        title={t`Previous Page`}
        onClick={handlePreviousPage}
        disabled={currentPageNumber <= 1}>
        <ChevronLeft width={20} height={20} />
      </Button>
      {isEditing ? (
        <PageText as='span'>
          {pageLabel}{' '}
          <PageInput
            ref={inputRef}
            $charWidth={inputValue.length + 2}
            inputMode='numeric'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSubmit}
          />
          {pageSuffix}
        </PageText>
      ) : (
        <PageText onClick={handleStartEdit}>
          {pageLabel} {currentPageNumber}
          {pageSuffix}
        </PageText>
      )}
      <Button
        variant='ghost'
        size='icon-lg'
        title={t`Next Page`}
        onClick={handleNextPage}
        disabled={currentPageNumber >= totalPages}>
        <ChevronRight width={20} height={20} />
      </Button>
    </PageSelectorContainer>
  )
}
